import theme from "@/assets/theme/antd.default.theme.json";
import { DESCENDING } from "@/components/tables/shared-table-state";
import {
	addSortOrderToColumns,
	handleTableSort,
} from "@/utils/table-sort-utils";
import { Space, type TablePaginationConfig, type TableProps, Tag } from "antd";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import type { Dispatch, Key } from "react";
import type {
	SubmissionTableAction,
	SubmissionTableState,
} from "../SubmissionsTable";
interface ConfigureTableOptions<T> {
	handleRowClick?: (record: T) => void;
	dataSource?: T[];
	onChange?: (pagination: any, filters: any, sorter: any) => void;
	rowSelection?: TableProps<T>["rowSelection"];
	rowKey?: string | ((record: T) => string);
	pagination?: Partial<TableProps<T>["pagination"]>;
}

export function getTableProps<T>(
	state: Partial<SubmissionTableState>,
	dispatch: Dispatch<SubmissionTableAction>,
	options: ConfigureTableOptions<T> = {},
): TableProps<T> {
	const {
		pageNum,
		pageSize,
		count,
		isLoading,
		columnsShown,
		sortBy,
		sortOrder,
		dataSource,
		tableColumns,
	} = state;

	// Define the default onChange handler using unified sorting utilities
	const defaultOnChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue | null>,
		sorter: SorterResult<T> | SorterResult<T>[],
	) => {
		// Handle pagination
		if (pagination.current && pagination.current !== pageNum) {
			dispatch({ type: "SET_PAGE_NUM", payload: pagination.current });
		}
		if (pagination.pageSize && pagination.pageSize !== pageSize) {
			dispatch({ type: "SET_PAGE_SIZE", payload: pagination.pageSize });
		}

		// Handle filters
		const cleanedFilters: Record<string, FilterValue | null> = {};
		for (const key in filters) {
			if (filters[key] !== null) {
				cleanedFilters[key] = filters[key];
			}
		}
		dispatch({ type: "SET_FILTERS", payload: cleanedFilters });

		// Handle sorting using the unified utility
		handleTableSort(sorter, dispatch);
	};

	const defaultRowSelection: TableProps<T>["rowSelection"] = {
		selectedRowKeys: state.checkedItems,
		onChange: (selectedRowKeys: Key[], _selectedRows: T[]) => {
			dispatch({ type: "SET_CHECKED_ITEMS", payload: selectedRowKeys });
		},
	};

	// Merge default and custom pagination options
	const pagination: TableProps<T>["pagination"] = {
		current: pageNum,
		pageSize,
		total: count,
		showTotal: (total, range) => (
			<Space>
				{Array.isArray(state?.filters?.asset_type_name)
					? state.filters.asset_type_name.map((type: string) => (
							<Tag key={type} color={theme.token.colorPrimary}>
								{type}
							</Tag>
						))
					: ""}
				<div style={{ display: "flex", alignItems: "center" }}>
					<span>{`Showing ${range[0].toLocaleString()} - ${range[1].toLocaleString()} of ${total.toLocaleString()} items`}</span>
				</div>
			</Space>
		),
		pageSizeOptions: ["5", "10", "20", "50"],
		showSizeChanger: true,
		onChange: (page: number) => {
			dispatch({ type: "SET_PAGE_NUM", payload: page });
		},
		onShowSizeChange: (_current: number, size: number) => {
			dispatch({ type: "SET_PAGE_SIZE", payload: size });
			dispatch({ type: "SET_PAGE_NUM", payload: 1 });
		},
		...options.pagination, // Allow custom pagination options to override defaults
	};

	// Filter visible columns and add sort order indicators for controlled sorting
	// This ensures the correct sort arrow appears on the active column header
	const visibleColumns = (tableColumns || []).filter(
		(col) => columnsShown?.[col.key as string] !== false,
	);

	const columnsWithSortOrder = addSortOrderToColumns(
		visibleColumns,
		sortBy || "",
		sortOrder || DESCENDING,
	);

	const adjustedDataSource = isLoading
		? new Array(pageSize || 10).fill({ isLoading: true })
		: (dataSource || []).map((item) => ({ ...item, isLoading: false }));

	// Construct the table props
	const tableProps: TableProps<T> = {
		dataSource: adjustedDataSource,
		columns: columnsWithSortOrder,
		pagination,
		loading: isLoading, // Show Ant Design's default spinner when loading
		rowKey: options?.rowKey || "id",
		scroll: { x: true },
		tableLayout: "auto",
		onChange: options.onChange || defaultOnChange,
		rowSelection: (options.rowSelection ||
			defaultRowSelection) as TableProps<T>["rowSelection"],
	};

	// Handle custom row click
	if (options.handleRowClick) {
		tableProps.onRow = (record) => ({
			onClick: () => options.handleRowClick?.(record),
		});
	}

	return tableProps;
}
